import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";

interface DeleteWorkflowModalProps {
  isOpen: boolean;
  workflowId: string;
  workflowName: string;
  onClose: () => void;
  onConfirm: (id: string) => Promise<void>;
}

export const DeleteWorkflowModal: React.FC<DeleteWorkflowModalProps> = ({
  isOpen,
  workflowId,
  workflowName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await onConfirm(workflowId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete workflow");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader className="text-brand-primary dark:!text-white font-bold">
          {t("Confirm Delete")}
        </ModalHeader>
        <ModalBody>
          {t("Are you sure you want to delete the workflow")} "{workflowName}"?
          <p className="text-sm text-danger mt-2">
            {t("This action cannot be undone. All workflow data will be permanently removed.")}
          </p>
          {error && (
            <p className="text-sm text-danger mt-4" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button isDisabled={isLoading} variant="flat" onPress={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            color="danger"
            isDisabled={isLoading}
            isLoading={isLoading}
            onPress={handleConfirm}
          >
            {t("Delete")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
